import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { useSurveyStore } from "@stores/survey/survey";
import { POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";
import { useViewPopulations } from "@/composables/useViewPopulations";
import { supportsSubtotals } from "@/composables/useChartRules";
import { useDecodeMessage } from "@/utils/composables/decodeMessage";

export function resolveLocalizedTitle(title, localeToken = null) {
  if (!title) return "";
  let resolved;
  if (typeof title === "string") resolved = title;
  else if (typeof title === "object") resolved = (localeToken && title[localeToken]) || Object.values(title)[0] || "";
  else resolved = String(title);
  return useDecodeMessage().decodeName(resolved);
}

export function computeSentimentBreakdown(sentimentData) {
  if (!sentimentData) return null;
  const pos = sentimentData.positive?.nb || 0;
  const neu = sentimentData.neutral?.nb || 0;
  const neg = sentimentData.negative?.nb || 0;
  const total = pos + neu + neg;
  if (total === 0) return null;
  return { positive: pos / total, neutral: neu / total, negative: neg / total };
}

/**
 * Core card data composable — resolves card metadata, loop iterations,
 * raw chart data, and shared option-derived computeds.
 */
export function useCardData(card, localOptions = null) {
  const { decodeName } = useDecodeMessage();
  const store = useResultsV2Store();
  const { availableCards, chartsData, significance, currentView } = storeToRefs(store);

  function getSignificance(questionId, popKey, code) {
    if (!popKey || !questionId) return null;
    return significance.value?.[questionId]?.[popKey]?.[code] ?? null;
  }

  const localesStore = useSurveyLocalesStore();
  const { analysisLocale } = storeToRefs(localesStore);

  const surveyStore = useSurveyStore();
  const { areWeightsEnabled } = storeToRefs(surveyStore);

  const shouldIncludeWeights = computed(() => {
    if (!areWeightsEnabled.value) return false;
    return currentView.value?.options?.apply_weightings ?? true;
  });

  const displayLocale = computed(
    () => currentView.value?.options?.locale_token || analysisLocale.value?.token || null,
  );

  // --- Card options ---

  const includeSkips = computed(() => {
    const opts = localOptions?.value || card.value?.options;
    return opts?.include_skips ?? false;
  });

  const hasCustomBase = computed(() => {
    const opts = localOptions?.value || card.value?.options;
    return Boolean(opts?.custom_base);
  });

  const rankingTopN = computed(() => {
    if (questionMeta.value?.type !== "q_ranking") return null;
    const opts = localOptions?.value || card.value?.options;
    const n = Number(opts?.ranking_top_n);
    return n > 0 ? n : null;
  });

  const subtotals = computed(() => {
    const opts = localOptions?.value || card.value?.options;
    if (!opts?.subtotals_enabled) return null;
    if (!supportsSubtotals(questionMeta.value)) return null;
    return opts?.subtotals || null;
  });

  const includeSentiment = computed(() => {
    const opts = localOptions?.value || card.value?.options;
    return opts?.include_sentiment ?? true;
  });

  const choiceColors = computed(() => {
    const opts = localOptions?.value || card.value?.options;
    return opts?.choice_colors || null;
  });

  const radarOptions = computed(() => {
    const opts = localOptions?.value || card.value?.options;
    return opts?.radar || {};
  });

  // --- Populations (shared with the analysis cards, see useViewPopulations) ---

  const {
    resolvedPopulations,
    displayedPopulations: matrixPopulations,
    selectedPopulation: singlePopulation,
  } = useViewPopulations(card, localOptions);

  // --- Question metadata ---

  const questionMeta = computed(() => {
    return availableCards.value.find((q) => q.id === card.value?.card_id) || null;
  });

  const questions = computed(() => questionMeta.value?.questions || []);
  const specs = computed(() => questionMeta.value?.specs || {});

  const selectedLoopIndex = computed(() => {
    const opts = localOptions?.value || card.value?.options;
    return opts?.loop_index ?? null;
  });

  const loopIterations = computed(() => {
    const withLoop = questions.value.filter((q) => q.loop_index != null);
    if (!withLoop.length) return [];
    const byIndex = new Map();
    for (const q of withLoop) {
      if (!byIndex.has(q.loop_index)) byIndex.set(q.loop_index, []);
      byIndex.get(q.loop_index).push(q);
    }
    return [...byIndex.entries()]
      .sort(([a], [b]) => a - b)
      .map(([loopIndex, group]) => {
        const first = group.reduce((a, b) => (a.rank <= b.rank ? a : b));
        return {
          index: loopIndex,
          title: first.loop_details?.loop_title,
          question_id: first.id,
          question_ids: group.sort((a, b) => a.rank - b.rank).map((q) => q.id),
        };
      });
  });

  const activeIteration = computed(() => {
    if (!loopIterations.value.length) return null;
    if (selectedLoopIndex.value != null) {
      const match = loopIterations.value.find((it) => it.index === selectedLoopIndex.value);
      if (match) return match;
    }
    return loopIterations.value[0];
  });

  const loopIterationOptions = computed(() =>
    loopIterations.value.map((it) => ({
      value: it.index,
      label: resolveLocalizedTitle(it.title, displayLocale.value) || `Iteration ${it.index}`,
    })),
  );

  // A matrix recode (q_hidden) has no matrix_type / items in its specs: its shape comes from its questions.
  const isMatrixRecode = computed(
    () => questionMeta.value?.type === "q_hidden" && questions.value.some((q) => q.question_type === "matrix"),
  );
  const isMatrix = computed(() => !!specs.value.matrix_type || isMatrixRecode.value);
  const isMatrixMultiple = computed(
    () => specs.value.matrix_type === "multiple" || (isMatrixRecode.value && questions.value[0]?.matrix_type === "multiple"),
  );

  const matrixItems = computed(() => {
    if (!isMatrixRecode.value) return specs.value.items || [];
    const seen = new Set();
    return questions.value
      .filter((q) => q.item_token && !seen.has(q.item_token) && seen.add(q.item_token))
      .map((q) => ({ token: q.item_token, title: q.item_title }));
  });

  const items = computed(() => {
    if (!isMatrix.value) return [];
    // In a loop, each item_token has one question per iteration — keep only the
    // active iteration's questions, else fromEntries keeps the last iteration's.
    const sourceQuestions = activeIteration.value
      ? questions.value.filter((q) => activeIteration.value.question_ids.includes(q.id))
      : questions.value;
    const questionByItem = Object.fromEntries(sourceQuestions.map((q) => [q.item_token, q]));
    return matrixItems.value.map((item) => {
      const q = questionByItem[item.token];
      return {
        id: q?.id,
        item_title: item.title,
        item_name: q?.name,
        loop_index: q?.loop_index,
      };
    });
  });

  const activeItems = computed(() => {
    if (!items.value.length || !activeIteration.value) return items.value;
    return items.value.filter((item) => activeIteration.value.question_ids.includes(item.id));
  });

  const cardType = computed(() => questionMeta.value?.type || null);

  const effectiveQuestionId = computed(() => {
    if (!questionMeta.value) return null;
    if (activeIteration.value) return activeIteration.value.question_id;
    if (cardType.value === "q_ranking") {
      // _top_1 derived question has answers with rank=NULL; the original ranking question has rank values.
      const firstId = questions.value[0]?.id;
      const other = questions.value.find((q) => q.id !== firstId);
      return other?.id || firstId;
    }
    return questions.value[0]?.id || null;
  });

  const rawData = computed(() => {
    if (!effectiveQuestionId.value) return null;
    return chartsData.value[effectiveQuestionId.value] || null;
  });

  const codeLabels = computed(() => {
    const type = cardType.value;
    const s = specs.value;
    const map = {};

    const addAnswers = (answers) =>
      (answers || []).forEach((a, i) => {
        map[String(i + 1)] = resolveLocalizedTitle(a.title, displayLocale.value) || String(i + 1);
      });
    const addRange = (min, max) => {
      for (let n = min; n <= max; n++) map[String(n)] = String(n);
    };

    if (["q_single", "q_multiple", "q_ranking", "q_image_choice", "q_image_choice_multiple", "q_hidden"].includes(type)) {
      addAnswers(s.answers);
    } else if (type === "q_matrix") {
      if (["single", "multiple"].includes(s.matrix_type)) addAnswers(s.answers);
      else if (s.matrix_type === "rating") addRange(s.rating_limits?.min ?? 1, s.rating_limits?.max ?? 10);
      else if (s.matrix_type === "nps") addRange(0, 10);
    } else if (type === "q_rating") {
      addRange(s.rating_limits?.min ?? 1, s.rating_limits?.max ?? 10);
    } else if (type === "q_slider") {
      addRange(s.slider_limits?.min ?? 0, s.slider_limits?.max ?? 10);
    } else if (type === "q_nps") {
      addRange(0, 10);
    }
    return map;
  });

  const codeImages = computed(() => {
    if (!["q_image_choice", "q_image_choice_multiple"].includes(cardType.value)) return {};
    const map = {};
    (specs.value.answers || []).forEach((a, i) => {
      const url = resolveLocalizedTitle(a.url, displayLocale.value);
      if (url) map[String(i + 1)] = url;
    });
    return map;
  });

  const cardName = computed(() => {
    if (!questionMeta.value) return "";
    return decodeName(questionMeta.value.name || questionMeta.value.token || "");
  });

  const title = computed(() => {
    if (!questionMeta.value) return "";
    return resolveLocalizedTitle(specs.value.question, displayLocale.value);
  });

  // When "All" is hidden and a single population is displayed, the header n=
  // must reflect that population, not the full base.
  const respondents = computed(() => {
    const { showAll, pops } = resolvedPopulations.value;
    if (!showAll && pops.length > 0) {
      const popRaw = rawData.value?.[pops[0].key];
      if (popRaw) return getCardBase(popRaw);
    }
    return getCardBase(rawData.value);
  });

  // --- Sentiment ---

  const hasSentimentData = computed(() => {
    if (!rawData.value?.answers) return false;
    return Object.values(rawData.value.answers).some((a) => a.sentiment_data);
  });

  const showSentiment = computed(() =>
    (["q_open", "q_open_multiple"].includes(cardType.value) || questionMeta.value?.is_recode_question) &&
    includeSentiment.value &&
    hasSentimentData.value,
  );

  // --- Numeric ---

  const isNumeric = computed(() => cardType.value === "q_numeric");

  const numericStats = computed(() => {
    if (!isNumeric.value || !rawData.value?.numeric_stats) return null;

    const { showAll, pops } = resolvedPopulations.value;
    const result = [];

    if (showAll) {
      result.push({
        label: "All",
        color: POPULATION_AUTO_COLORS[0],
        mean: rawData.value.numeric_stats.mean,
        median: rawData.value.numeric_stats.median,
      });
    }

    for (const pop of pops) {
      const popRaw = rawData.value[pop.key];
      if (!popRaw?.numeric_stats) continue;
      result.push({
        label: pop.label,
        color: pop.color,
        mean: popRaw.numeric_stats.mean,
        median: popRaw.numeric_stats.median,
      });
    }

    return result;
  });

  // --- Shared helpers ---

  function getAnswerCount(answer) {
    if (!answer) return 0;
    return (shouldIncludeWeights.value ? answer.weighted_nb : answer.nb) || 0;
  }

  function getSkippedCount(source) {
    return (shouldIncludeWeights.value ? source?.skipped_weighted_respondents : source?.skipped_respondents) || 0;
  }

  function getAnswerPercentage(answer) {
    if (!answer) return 0;
    return (shouldIncludeWeights.value ? answer.weighted_percentage : answer.percentage) || 0;
  }

  function getAnswerDisplayOrder(answer) {
    const order = shouldIncludeWeights.value ? answer?.weighted_display_order : answer?.display_order;
    return order ?? Number.MAX_SAFE_INTEGER;
  }

  function getAdjustedBase(source) {
    if (!source) return 0;
    const base = (shouldIncludeWeights.value
      ? source.weighted_respondents
      : source.respondents) || 0;
    if (!includeSkips.value || hasCustomBase.value) return base;
    return base + getSkippedCount(source);
  }

  function getCardBase(source) {
    return getAdjustedBase(isMatrix.value ? source?.matrix_base ?? source : source);
  }

  function buildSkipEntry(source, base) {
    const nb = getSkippedCount(source);
    if (nb === 0) return null;
    return {
      code: "999",
      label: "Skip",
      value: base > 0 ? nb / base : 0,
      count: nb,
      significance: null,
      significanceDetails: null,
    };
  }

  function appendSubtotalBars(data, subtotalConfigs) {
    if (!subtotalConfigs?.length) return data;
    const result = [...data];

    subtotalConfigs.forEach((config, index) => {
      if (!config.codes?.length || !config.label) return;
      const matching = data.filter((d) => config.codes.includes(d.code));
      if (!matching.length) return;

      result.push({
        code: `subtotal_${index}`,
        label: config.label,
        value: matching.reduce((sum, d) => sum + d.value, 0),
        count: matching.reduce((sum, d) => sum + d.count, 0),
        significance: null,
        significanceDetails: null,
        isSubtotal: true,
        subtotalOf: matching.map((d) => d.label),
      });
    });

    return result;
  }

  function topRanksCount(answer, n) {
    let count = 0;
    for (let rank = 1; rank <= n; rank++) count += getAnswerCount(answer.ranks?.[rank]);
    return count;
  }

  function buildBarData(answers, adjustedBase = null, withSentiment = false, popKey = null, questionId = null) {
    if (!answers) return [];
    const qId = questionId ?? effectiveQuestionId.value;
    return Object.entries(answers)
      .sort(([, a], [, b]) => getAnswerDisplayOrder(a) - getAnswerDisplayOrder(b))
      .map(([code, answer]) => {
        const nb = rankingTopN.value
          ? topRanksCount(answer, rankingTopN.value)
          : (shouldIncludeWeights.value ? answer.weighted_nb : answer.nb) || 0;
        const pct = (shouldIncludeWeights.value ? answer.weighted_percentage : answer.percentage) || 0;
        const sig = rankingTopN.value ? null : getSignificance(qId, popKey, code);
        const item = {
          code,
          label: codeLabels.value[code] || resolveLocalizedTitle(answer.title, displayLocale.value) || code,
          value: adjustedBase != null && adjustedBase > 0
            ? nb / adjustedBase
            : pct,
          count: nb,
          significance: sig?.direction ?? null,
          significanceDetails: sig,
        };
        if (codeImages.value[code]) item.image = codeImages.value[code];
        if (withSentiment) {
          item.sentimentBreakdown = computeSentimentBreakdown(answer.sentiment_data);
        }
        return item;
      });
  }

  return {
    // Store refs (needed by sub-composables)
    chartsData,
    displayLocale,
    // Card metadata (native shape)
    questionMeta,
    cardType,
    codeLabels,
    codeImages,
    rawData,
    isMatrix,
    isMatrixMultiple,
    cardName,
    title,
    activeIteration,
    loopIterationOptions,
    activeItems,
    effectiveQuestionId,
    // Options
    includeSkips,
    rankingTopN,
    subtotals,
    choiceColors,
    radarOptions,
    showSentiment,
    resolvedPopulations,
    matrixPopulations,
    singlePopulation,
    // Counts
    respondents,
    isNumeric,
    numericStats,
    shouldIncludeWeights,
    // Helpers (functions used by sub-composables)
    getAdjustedBase,
    getCardBase,
    getSkippedCount,
    getAnswerCount,
    getAnswerPercentage,
    getAnswerDisplayOrder,
    buildSkipEntry,
    appendSubtotalBars,
    buildBarData,
    getSignificance,
  };
}
